import SideMenu from '../components/Menu';
import { useEffect, useState } from 'react';
import AddPaperModal from '../components/modals/AddPaperModal';
import WarningModal from '../components/modals/WarningModal';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';

export interface Author {
  id: string;
  name: string;
  isOwner?: boolean;
}

export interface Paper {
  _id: string;
  paperId: string;
  title: string;
  abstract: string;
  authors: Author[];
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

const UnpublishedPapers = () => {
  const { user } = useAuth();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [warningModal, setWarningModal] = useState<boolean>(false);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Paper | null>(null);
  const { studentId } = useParams<{ studentId: string }>();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/unpublishedpapers/get/${
          studentId ? studentId : ''
        }`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setLoading(false);

      if (!response.ok) {
        throw new Error('Failed to fetch papers');
      }

      setPapers(await response.json());
      console.log('Papers:', await response.json());
    } catch (error) {
      console.error('Error fetching papers:', error);
    }
  };

  const deletePaper = (paper: Paper) => async () => {
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/unpublishedpapers/remove/${paper.paperId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete paper');
      }

      fetchData();
    } catch (error) {
      console.error('Error deleting paper:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const visibleFields = ['title', 'abstract'];

  const activateEditModal = (student: Paper) => {
    if (student) {
      setSelectedStudent(student);
      setModalVisible(true);
    }
  };

  const activateAddModal = () => {
    setSelectedStudent(null);
    setModalVisible(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <i className="fa-solid fa-spinner animate-spin text-blue-500 text-3xl"></i>
      </div>
    );
  }

  return (
    <>
      <SideMenu />
      <div id="unpublished-papers" className="w-full h-screen bg-accent p-3 overflow-hidden">
        <div className="bg-white rounded-lg h-full flex flex-col p-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-center">Unpublished Papers</h1>
            <button
              onClick={() => activateAddModal()}
              className="text-xs bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              Add Paper
            </button>
          </div>
          <div className="overflow-auto mt-4">
            <table className="min-w-full bg-white">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-xs border-b">Title</th>
                  <th className="px-4 py-2 text-xs border-b">Abstract</th>
                  <th className="px-4 py-2 text-xs border-b">Authors</th>{' '}
                  <th className="px-4 py-2 text-xs border-b">Owner</th>
                  <th className="px-4 py-2 text-xs border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {papers.map(paper => (
                  <tr key={paper._id}>
                    {visibleFields.map(field => (
                      <td key={field} className="px-4 py-2 text-xs border-b text-center">
                        {field.toLowerCase().includes('date')
                          ? new Date(paper[field as keyof Paper] as string).toLocaleDateString(
                              'en-GB',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit',
                              }
                            )
                          : (paper[field as keyof Paper] as string) || 'N/A'}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-xs border-b text-center">
                      {paper.authors.map((author: { id: string; name: string }) => (
                        <div
                          key={author.id}
                          className="text-xs inline-block px-2 py-1 mx-1 rounded-md border border-blue-500"
                        >
                          {author.name}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-2 text-xs border-b text-center">
                      {user &&
                      paper.authors?.some(
                        (author: Author) => author.id === user._id && author.isOwner
                      ) ? (
                        <i className="fa-solid fa-check text-green-500"></i>
                      ) : (
                        <i className="fa-solid fa-xmark text-red-500"></i>
                      )}
                    </td>

                    <td className="px-4 py-2 text-xs border-b text-center">
                      {user &&
                        paper.authors?.some(
                          (author: Author) => author.id === user._id && author.isOwner
                        ) && (
                          <>
                            <button
                              onClick={() => activateEditModal(paper)}
                              className="text-blue-500 hover:underline"
                            >
                              Edit
                            </button>
                            <br />
                            <button
                              onClick={() => {
                                setWarningModal(true);
                                setSelectedPaper(paper);
                              }}
                              className="text-red-500 hover:underline"
                            >
                              Delete
                            </button>
                          </>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="position-absolute text-center">
            <p className="font-light text-xs text-gray-500 mt-4">
              Your ID: <br />
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(user?._id as string);
              }}
              className="border text-xs border-opacity-25 inline rounded-lg border-blue-500 p-0.5 px-2 font-bold text-blue-500 transition-all duration-150 ease-in-out active:scale-95"
            >
              {user?._id}
              <i className="fa-solid fa-copy text-xs ml-2 cursor-pointer"></i>
            </button>
          </div>
        </div>
      </div>
      <AddPaperModal
        onClose={() => setModalVisible(false)}
        visible={modalVisible}
        initialValues={selectedStudent}
        onPaperAdded={() => {
          fetchData();
        }}
      />
      <WarningModal
        visible={warningModal}
        description="Are you sure you want to delete this paper?"
        onCancel={() => setWarningModal(false)}
        onConfirm={() => {
          if (selectedPaper) {
            deletePaper(selectedPaper)();
          }
          setWarningModal(false);
          setSelectedPaper(null);
        }}
      />
    </>
  );
};

export default UnpublishedPapers;
